import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import ConfirmationModal from "../../Common/ConfirmationModal";
import API from "../../../util/api";
import { FILES_BASE_URL } from "../../../util/constant";

const STAT_REQUIREMENT_DEBOUNCE_MS = 150;
const statRequirementInFlightByKey = new Map();

const getStatRequirementCacheKey = (invoiceId, customerId) =>
  `${String(invoiceId ?? "").trim()}::${String(customerId ?? "").trim()}`;

const getStatImportRequirement = (invoiceId, customerId) => {
  const requestKey = getStatRequirementCacheKey(invoiceId, customerId);
  if (!requestKey || requestKey === "::") {
    return Promise.resolve(null);
  }

  const inFlight = statRequirementInFlightByKey.get(requestKey);
  if (inFlight) {
    return inFlight;
  }

  const requestPromise = API.checkSTATImportRequirement({
    invoice_id: invoiceId,
    customer_id: customerId,
  })
    .then((response) => response || null)
    .finally(() => {
      statRequirementInFlightByKey.delete(requestKey);
    });

  statRequirementInFlightByKey.set(requestKey, requestPromise);
  return requestPromise;
};

const Panel = styled.div`
  flex: 1;
`;

const Notification = styled.div`
  background: white;
  border-bottom: 2px solid ${({ colors }) => colors.border};
  border-radius: 0;
  padding: 1em;
  font-size: 0.95em;
  color: ${({ colors }) => colors.labelText};

  &:first-child {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }

  &:last-child {
    border-bottom: none;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }

  strong {
    display: block;
    line-height: 1.4;
  }
`;

const POCCard = styled(Notification)`
  font-size: 1.1em;
  line-height: 1.6;
  h2 {
    margin: 0 0 0.5em;
    font-size: 1.5em;
  }
`;

const Button = styled.button`
  display: inline-block;
  margin-top: 0.5em;
  margin-right: 0.5em;
  padding: 0.5em 0.8em;
  background: ${({ colors }) => colors.buttonBackground};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.9em;
  font-weight: bold;
  cursor: pointer;

  &:hover {
    background: ${({ colors }) => colors.buttonHoverBackground};
  }
`;

const ActionsBlock = styled.div`
  margin-top: 1em;

  > div + div {
    margin-top: 0.75em;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75em;
`;

const Separator = styled.span`
  font-weight: 600;
  color: ${({ colors }) => colors.labelText};
`;

export default function SubscriptionAdminDashboardContent({
  colors,
  onViewMore,
}) {
  const navigate = useNavigate();

  // Redux state
  const pocDetail =
    useSelector((s) => s.auth.subscription_details?.customerDetail) || {};
  const invoiceList =
    useSelector((s) => s.auth.subscription_details?.invoiceDetail) || [];
  const selected = useSelector((s) => s.auth.selected_subscriptions) || {};
  const { invoice_id, customer_id } = selected;

  // STAT requirement state
  const [statRequirement, setStatRequirement] = useState(null);
  const [statRequirementLoading, setStatRequirementLoading] = useState(true);
  const activeStatRequestKeyRef = React.useRef("");

  // reimport modal state
  const [reimportOpen, setReimportOpen] = useState(false);
  const [reimportMsg, setReimportMsg] = useState("");
  const [reimportStatus, setReimportStatus] = useState("Success");

  const handleDownloadBlankStat = () => {
    window.location.href = `${FILES_BASE_URL}/ISAFE_STAT_Sheet.csv`;
  };

  useEffect(() => {
    let isCancelled = false;
    let debounceTimer = null;
    const requestKey = getStatRequirementCacheKey(invoice_id, customer_id);
    activeStatRequestKeyRef.current = requestKey;

    const fetchRequirement = async () => {
      if (!invoice_id || !customer_id) {
        setStatRequirement(null);
        setStatRequirementLoading(false);
        return;
      }

      setStatRequirementLoading(true);
      try {
        const resp = await getStatImportRequirement(invoice_id, customer_id);
        if (
          !isCancelled &&
          activeStatRequestKeyRef.current === requestKey
        ) {
          setStatRequirement(resp || null);
        }
      } catch (err) {
        if (
          !isCancelled &&
          activeStatRequestKeyRef.current === requestKey
        ) {
          console.error("Failed to check STAT requirement:", err);
          setStatRequirement(null);
        }
      } finally {
        if (
          !isCancelled &&
          activeStatRequestKeyRef.current === requestKey
        ) {
          setStatRequirementLoading(false);
        }
      }
    };

    debounceTimer = setTimeout(fetchRequirement, STAT_REQUIREMENT_DEBOUNCE_MS);

    return () => {
      isCancelled = true;
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [invoice_id, customer_id]);

  // expiry text logic (unchanged)
  const latestInvoice =
    invoiceList.length > 0
      ? invoiceList.reduce((a, b) =>
          new Date(b.end_date) > new Date(a.end_date) ? b : a
        )
      : null;

  const formatShort = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${m}-${d}-${y}`;
  };
  let expiryText = "No subscription data.";
  if (latestInvoice) {
    const exp = formatShort(latestInvoice.end_date);
    expiryText =
      new Date() > new Date(latestInvoice.end_date)
        ? `Your subscription expired on ${exp}.`
        : `Your subscription will expire on ${exp}.`;
  }

  // POC display logic (unchanged)
  const [poc, setPoc] = useState({ name: "", email: "", phone: "" });
  const {
    contact_name,
    contact_email,
    contact_phone,
    change_poc_name,
    change_poc_email,
    change_poc_phone,
  } = pocDetail;

  useEffect(() => {
    const rawName = change_poc_name || contact_name || "";
    const rawEmail = change_poc_email || contact_email || "";
    const rawPhone = change_poc_phone || contact_phone || "";

    let formattedPhone = rawPhone;
    if (/^\d{10}$/.test(rawPhone)) {
      formattedPhone = `(${rawPhone.slice(0, 3)}) ${rawPhone.slice(
        3,
        6
      )}-${rawPhone.slice(6)}`;
    }

    const name =
      change_poc_name && change_poc_name !== ""
        ? `${rawName} (pending approval)`
        : rawName;
    const email =
      change_poc_email && change_poc_email !== ""
        ? `${rawEmail} (pending approval)`
        : rawEmail;
    const phone =
      change_poc_phone && change_poc_phone !== ""
        ? `${formattedPhone} (pending approval)`
        : formattedPhone;

    setPoc((prev) =>
      prev.name === name && prev.email === email && prev.phone === phone
        ? prev
        : { name, email, phone }
    );
  }, [
    contact_name,
    contact_email,
    contact_phone,
    change_poc_name,
    change_poc_email,
    change_poc_phone,
  ]);

  // “Reimport STAT Sheet” handler
  const handleReimport = async () => {
    try {
      const resp = await API.reimportStatSheet({ customer_id, invoice_id });
      const status = resp?.status === "Failed" ? "Failed" : "Success";
      setReimportMsg(resp.message || (status === "Failed" ? "Reimport failed." : "Reimport completed."));
      setReimportStatus(status);
    } catch (err) {
      console.error("Reimport failed:", err);
      setReimportMsg(err.message || "Reimport failed.");
      setReimportStatus("Failed");
    } finally {
      setReimportOpen(true);
    }
  };

  const handleReimportClose = () => {
    setReimportOpen(false);
    setReimportStatus("Success");
  };

  const shouldShowStatNotification =
    !statRequirementLoading &&
    statRequirement &&
    !!statRequirement.from_invoice_id &&
    !!statRequirement.to_invoice_id;

  return (
    <Panel>
      <Notification colors={colors}>
        <strong>
          {expiryText}
          <Button
            colors={colors}
            onClick={() =>
              navigate("/admincentral/subscriptions", {
                state: { defaultTab: "Renew Subscription" },
              })
            }
          >
            Request A Quote
          </Button>
        </strong>
      </Notification>

      <POCCard colors={colors}>
        <h2>Your Point of Contact</h2>
        {poc.name || "—"}
        <br />
        {poc.email ? <a href={`mailto:${poc.email}`}>{poc.email}</a> : "—"}
        <br />
        {poc.phone ? (
          <a href={`tel:${poc.phone.replace(/[^\d]/g, "")}`}>{poc.phone}</a>
        ) : (
          "—"
        )}
        <br />
        <Button colors={colors} onClick={onViewMore}>
          View More
        </Button>
      </POCCard>

      <Notification colors={colors}>
        You are viewing a <b>current subscription</b>. To switch to another
        subscription, select it from Subscription History in the top right
        corner.
      </Notification>

      <Notification colors={colors}>
        Teachers are <b>required</b> to certify their implementations. To change
        this setting, click on <b>Teachers &gt; Settings</b>, then change the
        option for <b><i>"Teachers Must Certify Implementations."</i></b>
      </Notification>

      {shouldShowStatNotification && (
        <Notification colors={colors}>
          You have not yet uploaded a <b>STAT Sheet for your currently valid subscription</b>. Click below to begin:
          <ActionsBlock>
            <ButtonRow>
              <Button
                colors={colors}
                onClick={() =>
                  navigate("/admincentral/teachers", {
                    state: { mode: "import" },
                  })
                }
              >
                Upload a STAT sheet
              </Button>
            </ButtonRow>
            <ButtonRow>
              <Button colors={colors} onClick={handleDownloadBlankStat}>
                Download STAT sheet Template
              </Button>
              <Separator colors={colors}>OR</Separator>
              <Button colors={colors} onClick={handleReimport}>
                Reimport Previous STAT Sheet
              </Button>
            </ButtonRow>
          </ActionsBlock>
        </Notification>
      )}

      {/* Reimport confirmation modal */}
      <ConfirmationModal
        show={reimportOpen}
        title="Reimport STAT Sheet"
        message={reimportMsg}
        onConfirm={handleReimportClose}
        onCancel={handleReimportClose}
        status={reimportStatus}
        single
      />
    </Panel>
  );
}
